import DOMPurify from "dompurify";

// Block dangerous URI schemes (javascript:, data: for non-image, vbscript:)
// that DOMPurify's default policy would otherwise allow in href/src.
const SAFE_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[#/])/i;

const HTTP_URL_REGEXP = /^https?:\/\//i;

/** True for an absolute http(s) URL, the only source an embed may load. */
export const isHttpUrl = (value: unknown): value is string =>
  typeof value === "string" && HTTP_URL_REGEXP.test(value.trim());

// An iframe only ever loads an absolute http(s) URL. A relative, `data:` or
// `javascript:` source (or none, which is about:blank) would share the
// widget's origin, i.e. the page the widget runs on — so such an iframe is
// removed, whatever policy let iframes through.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.nodeName === "IFRAME" && !isHttpUrl(node.getAttribute("src"))) {
    node.parentNode?.removeChild(node);
  }
});

/**
 * For inline HTML that needs to host click-actions (open-detail, open-url,
 * custom-action). Used by InlineHtml and ContentRenderer.
 */
export const sanitizeInline = (html: string): string =>
  DOMPurify.sanitize(html, {
    ADD_TAGS: ["style"],
    ADD_ATTR: [
      "data-action",
      "data-action-data",
      "data-open-detail",
      "data-payload",
    ],
    ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
    FORCE_BODY: true,
  });

/**
 * For server-validated custom component templates after placeholder
 * substitution. Narrower allowlist than sanitizeInline.
 */
export const sanitizeComponentTemplate = (html: string): string =>
  DOMPurify.sanitize(html, {
    ADD_TAGS: ["style"],
    ADD_ATTR: ["data-action", "data-action-data"],
    ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
  });

/**
 * For rich text other visitors wrote (changelog comments, feature request
 * descriptions and comments): their formatting, links and images with no
 * styles, scripts, embeds, form controls (fake sign-in forms), ids or data
 * attributes (no data-action triggers), and no unsafe links.
 */
export const sanitizeUserContent = (html: string): string =>
  DOMPurify.sanitize(html ?? "", {
    FORBID_TAGS: [
      "style",
      "iframe",
      "frame",
      "object",
      "embed",
      "form",
      "input",
      "button",
      "textarea",
      "select",
      "option",
      "label",
      "fieldset",
    ],
    FORBID_ATTR: ["style", "id", "action", "formaction"],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
  });

/**
 * For short project-configured texts rendered as HTML (reply times, the
 * out-of-office message): basic formatting and safe links only.
 */
export const sanitizeBasicFormatting = (html: string): string =>
  DOMPurify.sanitize(html ?? "", {
    ALLOWED_TAGS: ["b", "i", "strong", "em", "u", "br", "p", "a"],
    ALLOWED_ATTR: ["href", "target", "rel"],
    ALLOW_DATA_ATTR: false,
    // http(s), mailto, tel or relative. DOMPurify checks every attribute
    // value against this, so values without a scheme (`_blank`) must pass.
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });

/**
 * For chat message prose rendered from Markdown (Kai's and bots' string
 * content): the formatting, links (incl. `target`) and images stay; scripts,
 * event handlers and `javascript:` links go. The server sanitizes AI replies
 * before storing them; this keeps a stored string that skipped that from
 * running in the widget. No data attributes beyond the two the widget itself
 * reads (the source annotation title and protected media): a prompt-injected
 * `data-action` link would otherwise run the host app's custom actions.
 */
export const sanitizeMessageHtml = (html: string): string =>
  DOMPurify.sanitize(html, {
    ADD_ATTR: ["target", "data-title", "data-protected-file"],
    ALLOW_DATA_ATTR: false,
  });

/**
 * A plain-text message body (inbound email text) as bubble HTML: typed markup
 * such as a pasted `<iframe …>` stays text (#185493), newlines become breaks.
 * Only `<` is escaped: widget text arrives entity-encoded already.
 */
export const plainTextToMessageHtml = (text: string): string =>
  `${text ?? ""}`.replace(/</g, "&lt;").replace(/\n/g, "<br>");

// DOMPurify's default URI allowlist plus `gleap:` (in-widget links such as
// gleap://article/… and gleap://bot/…, handled by the click handlers).
const RICH_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|gleap):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

/**
 * For rich text the project wrote, rendered from TipTap documents or AI
 * answers (chat messages, help center articles, news, checklist tasks, help
 * center AI answers): the formatting, links, images, data-action buttons and
 * embeds (iframes with an absolute http(s) source, keeping their sandbox)
 * stay; scripts, event handlers and `javascript:` links go. Visitor-written
 * content goes through sanitizeUserContent instead.
 */
export const sanitizeRichHtml = (html: string): string =>
  DOMPurify.sanitize(html ?? "", {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: [
      "target",
      "allow",
      "allowfullscreen",
      "frameborder",
      "sandbox",
      "loading",
    ],
    ALLOWED_URI_REGEXP: RICH_URI_REGEXP,
  });

/**
 * Plain text of an HTML string, for previews (quote previews, conversation
 * list). Parsed into an inert DOMParser document: unlike innerHTML on a
 * detached element, nothing in it loads or runs (no image requests, no
 * onerror / onload handlers).
 */
export const htmlToText = (html: unknown): string => {
  if (html === null || html === undefined) return "";
  const source = String(html);
  if (source.indexOf("<") === -1 && source.indexOf("&") === -1) return source;
  const doc = new DOMParser().parseFromString(source, "text/html");
  return doc.body?.textContent ?? "";
};
